import { useMemo, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';
import { useClientFlags, type ClientFlags } from '@/config/client-flags';
import { hasFeature } from '@/config/features';
import { useAuthStore } from '@/features/auth/auth.store';
import { can } from '@/features/auth/roles';
import {
  flatNavItems,
  navigation,
  type FlatNavItem,
  type NavItem,
  type NavSection,
} from './nav.config';

/**
 * Filters the nav tree down to what the signed-in user may actually see, marks
 * what has not been built yet, and works out which entry the current URL
 * belongs to.
 *
 * All three used to be done badly: visibility was an `&&` chain wrapped around
 * every menu item, unbuilt pages did not exist as a concept, and the active
 * item was `defaultSelectedKeys={['home']}` — a *default*, so the sidebar
 * highlighted "home" no matter where you were.
 */

interface Visibility {
  roles: string[] | undefined;
  hasBusiness: boolean;
  flags: ClientFlags;
}

/** A nav item with its built/planned state resolved for rendering. */
export type ResolvedNavItem = Omit<NavItem, 'children'> & {
  /** True when neither this entry nor anything under it has been built. */
  planned: boolean;
  children?: ResolvedNavItem[];
};

export interface ResolvedNavSection extends Omit<NavSection, 'items'> {
  items: ResolvedNavItem[];
  planned: boolean;
}

function isVisible(item: NavItem, { roles, hasBusiness, flags }: Visibility) {
  if (!hasFeature(item.feature)) return false;
  if (item.flag && !flags[item.flag]) return false;
  if (item.permission && !can(roles, item.permission)) return false;
  if (item.requiresBusiness && !hasBusiness) return false;
  if (item.globalOnly && hasBusiness) return false;
  return true;
}

/**
 * Prune at any depth, and resolve `planned` on the way back up.
 *
 * A group whose children all disappear disappears with them — otherwise an
 * empty "Configuration" expands into nothing, which is what the original did
 * whenever the blob-storage config files were absent. A group counts as built
 * as soon as one descendant is, so a half-finished section still opens.
 */
function resolve(
  items: NavItem[],
  visibility: Visibility,
  showPlanned: boolean
): ResolvedNavItem[] {
  return items.reduce<ResolvedNavItem[]>((kept, item) => {
    if (!isVisible(item, visibility)) return kept;
    // A tab of another page: no row here, but still a breadcrumb and a hit in
    // the palette, both of which read the unfiltered tree.
    if (item.hideInSidebar) return kept;

    if (!item.children?.length) {
      // Drop the (empty) children key so the leaf satisfies ResolvedNavItem.
      const { children: _leaf, ...rest } = item;
      void _leaf;
      const planned = item.status !== 'built';
      // Unbuilt entries leave the tree entirely rather than being hidden with
      // a class. Hiding in CSS meant the rail and the expanded sidebar could
      // disagree about what was in them — two layouts of one list, only one of
      // which the rule reliably reached.
      if (planned && !showPlanned) return kept;
      kept.push({ ...rest, planned });
      return kept;
    }

    // Children inherit the parent's gates unless they set their own.
    const children = resolve(
      item.children.map((child) => ({
        ...item,
        ...child,
        children: child.children,
      })),
      visibility,
      showPlanned
    );

    if (children.length) {
      kept.push({
        ...item,
        children,
        planned: children.every((child) => child.planned),
      });
    }
    return kept;
  }, []);
}

function useVisibility(): Visibility {
  const roles = useAuthStore((s) => s.profile?.roles);
  const hasBusiness = useAuthStore((s) => s.business !== null);
  const flags = useClientFlags();

  return useMemo(
    () => ({ roles, hasBusiness, flags }),
    [roles, hasBusiness, flags]
  );
}

/**
 * Whether the roadmap is being reviewed in the browser.
 *
 * `document.documentElement.classList.add('show-planned')` still works — the
 * class is now read here rather than only styled, so unbuilt entries can be
 * left out of the tree instead of hidden inside it.
 */
function useShowPlanned() {
  return useSyncExternalStore(
    (onChange) => {
      const observer = new MutationObserver(onChange);
      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class'],
      });
      return () => observer.disconnect();
    },
    () => document.documentElement.classList.contains('show-planned'),
    () => false
  );
}

export function useVisibleNavigation(): ResolvedNavSection[] {
  const visibility = useVisibility();
  const showPlanned = useShowPlanned();

  return useMemo(
    () =>
      navigation
        .map((section) => {
          const items = resolve(section.items, visibility, showPlanned);
          return {
            ...section,
            items,
            planned: items.every((item) => item.planned),
          };
        })
        .filter((section) => section.items.length > 0),
    [visibility, showPlanned]
  );
}

/**
 * Every leaf the user may reach — the command palette's source.
 *
 * Unbuilt pages are excluded outright rather than hidden with a class: a search
 * result that navigates to a placeholder is worse than no result.
 */
export function useSearchableNavigation(): FlatNavItem[] {
  const visibility = useVisibility();

  return useMemo(
    () =>
      flatNavItems.filter(
        (item) => item.status === 'built' && isVisible(item, visibility)
      ),
    [visibility]
  );
}

/** Does this path fall under `to`? Exact for '/', boundary-aware otherwise. */
function covers(to: string, pathname: string) {
  if (to === '/') return pathname === '/';
  // The trailing slash is the boundary: `/orders` never matches
  // `/orders-archive`.
  return pathname === to || pathname.startsWith(`${to}/`);
}

/**
 * True when `to` is the current page.
 *
 * Longest match wins. Nav entries nest as paths as well as as menus — `/team`
 * holds the staff list while `/team/timesheets` is its own entry — so a plain
 * prefix test lights up both, which is what a bare `NavLink` does and why the
 * sidebar asks this instead. `/booking/events` still marks
 * `/booking/events/42` active, because no nav entry claims that URL.
 */
export function useIsActive() {
  const { pathname } = useLocation();

  return useMemo(() => {
    const best = flatNavItems
      .filter((item) => covers(item.to, pathname))
      .sort((a, b) => b.to.length - a.to.length)[0];

    return (to: string) => {
      // A group has no `to`; an empty prefix would otherwise match everything.
      if (!to || !covers(to, pathname)) return false;
      // No entry claims this URL more specifically, so this one owns it.
      return !best || best.to === to;
    };
  }, [pathname]);
}

/** True when anything beneath this item is the current page, at any depth. */
export function useContainsActive() {
  const isActive = useIsActive();

  return useMemo(() => {
    const walk = (item: ResolvedNavItem | NavItem): boolean => {
      if (item.to && isActive(item.to)) return true;
      return (item.children as (ResolvedNavItem | NavItem)[] | undefined)?.some(
        walk
      ) ?? false;
    };
    return walk;
  }, [isActive]);
}

/** The nav entry matching the current URL — used for the page title and crumbs. */
export function useActiveNavItem(): FlatNavItem | undefined {
  const { pathname } = useLocation();

  return useMemo(() => {
    const matches = flatNavItems.filter((item) => covers(item.to, pathname));
    // Longest match wins, so /booking/sales beats /booking.
    return matches.sort((a, b) => b.to.length - a.to.length)[0];
  }, [pathname]);
}
